/**
 * RelatedResources
 * Renders 3 curated Magazine articles tied to a service page.
 * Editorial mapping is intentional (not category-based) so each service
 * ships the most useful, on-topic recommendations.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock } from 'lucide-react';
import { getArticle } from '../lib/magazineApi';

// Service slug -> curated article slugs (3 max, order matters).
const RELATED_MAP = {
  'it-pos-systems': [
    'restaurant-tech-stack-2026',
    'cost-control-modern-restaurant',
    'case-study-multi-location-growth',
  ],
  'restaurant-revenue-growth': [
    'case-study-multi-location-growth',
    'dynamic-pricing-restaurants',
    'reduce-no-shows-restaurant-system',
  ],
  'digital-branding': [
    'ai-search-restaurant-discovery',
    'google-business-profile-2026',
    'online-reputation-playbook',
  ],
  'ai-and-automation': [
    'ai-agents-restaurant-operations',
    'whatsapp-restaurants-operating-guide',
    'ai-search-restaurant-discovery',
  ],
  'ai-agents-for-restaurants': [
    'ai-agents-restaurant-operations',
    'whatsapp-restaurants-operating-guide',
    'online-reputation-playbook',
  ],
  'index-ai': [
    'ai-search-restaurant-discovery',
    'google-business-profile-2026',
    'online-reputation-playbook',
  ],
  'google-business-profile': [
    'google-business-profile-2026',
    'online-reputation-playbook',
    'ai-search-restaurant-discovery',
  ],
  'catering-ai': [
    'ai-agents-restaurant-operations',
    'whatsapp-restaurants-operating-guide',
    'case-study-multi-location-growth',
  ],
  // Fallback list used when a service slug is not in the map.
  default: [
    'restaurant-tech-stack-2026',
    'ai-search-restaurant-discovery',
    'ai-agents-restaurant-operations',
  ],
};

function relatedSlugsFor(serviceSlug) {
  return RELATED_MAP[serviceSlug] || RELATED_MAP.default;
}

export default function RelatedResources({ serviceSlug, lang = 'en' }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const slugs = relatedSlugsFor(serviceSlug);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          slugs.map((s) => getArticle(s).catch(() => null))
        );
        if (!cancelled) {
          setArticles(results.filter(Boolean));
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceSlug]);

  if (!loading && articles.length === 0) return null;

  const heading = lang === 'es' ? 'Recursos Relacionados' : 'Related Resources';
  const eyebrow = lang === 'es' ? 'DESDE EL MAGAZINE' : 'FROM THE MAGAZINE';
  const readMore = lang === 'es' ? 'Leer artículo' : 'Read article';
  const seeAll = lang === 'es' ? 'Ver todos los recursos' : 'Explore all resources';
  const readTime = lang === 'es' ? 'min lectura' : 'min read';

  return (
    <section className="py-20 lg:py-24 bg-neutral-50" data-testid="related-resources">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
          <div>
            <p className="inline-flex items-center gap-2 text-[#F77F11] text-xs tracking-[0.25em] font-semibold mb-3">
              <BookOpen className="w-3.5 h-3.5" /> {eyebrow}
            </p>
            <h2 className="text-3xl sm:text-[36px] font-bold text-neutral-900 tracking-[-0.02em]">
              {heading}
            </h2>
          </div>
          <Link
            to="/magazine"
            className="inline-flex items-center gap-1.5 text-[#F77F11] hover:text-[#d8651a] font-semibold text-sm"
            data-testid="related-resources-see-all"
          >
            {seeAll} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {loading &&
            [0, 1, 2].map((i) => (
              <div key={i} className="rounded-2xl bg-white border border-neutral-100 overflow-hidden animate-pulse">
                <div className="aspect-[16/10] bg-neutral-100" />
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-neutral-100 rounded w-2/3" />
                  <div className="h-5 bg-neutral-100 rounded w-full" />
                  <div className="h-3 bg-neutral-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          {!loading &&
            articles.slice(0, 3).map((a) => (
              <Link
                key={a.slug}
                to={`/magazine/${a.slug}`}
                className="group rounded-2xl bg-white border border-neutral-100 overflow-hidden hover:shadow-xl hover:border-[#F77F11]/30 transition-all"
                data-testid={`related-article-${a.slug}`}
              >
                <div className="aspect-[16/10] bg-neutral-100 overflow-hidden">
                  {a.cover_image ? (
                    <img
                      src={a.cover_image}
                      alt={a.cover_alt || a.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  ) : null}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase font-semibold text-neutral-500 mb-3">
                    <Clock className="w-3.5 h-3.5" /> {a.reading_time || 6} {readTime}
                  </div>
                  <h3 className="text-[19px] font-bold text-neutral-900 leading-snug tracking-[-0.01em] mb-2 group-hover:text-[#F77F11] transition-colors">
                    {a.title}
                  </h3>
                  {a.excerpt && (
                    <p className="text-sm text-neutral-600 leading-relaxed line-clamp-3">
                      {a.excerpt}
                    </p>
                  )}
                  <p className="mt-4 inline-flex items-center gap-1 text-[#F77F11] text-sm font-semibold">
                    {readMore} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </p>
                </div>
              </Link>
            ))}
        </div>
      </div>
    </section>
  );
}
